import type { ApiResponse, PageContent } from "src/api/common/apiTypes";
import { axiosInstance } from "src/api/axiosInstance";
import type { SpringPage } from "src/types/pageTypes";

export type DelegateFile = {
  fileId: number;
  originalName: string;
  savedName: string;
  fileUrl: string;
  fileSize: number;
  contentType: string;
  fileOrder: number;
};

export type DelegateItem = {
  id: number;
  delegateName: string;
  delegateDesc: string;
  delegateFileGrpId: number;
  files?: DelegateFile[];
  createdAt?: string;
  updatedAt?: string;
};

export type DelegateListItem = {
  id: number;

  // delegate
  delegateName?: string;
  delegateDesc?: string;
  delegateFileGrpId?: number;
  files?: DelegateFile[];

  createdAt?: string;
  updatedAt?: string;

  // brand (서버가 같이 줄 수 있음)
  brandCode?: string;
  brandCategoryId?: number;
  brandCategoryName?: string;
  brandNameKo?: string;
  brandNameEn?: string;
  brandDescription?: string;
  brandLogoFileGrpId?: number;
  brandImageFileGrpId?: number;
  officialWebsite?: string;
  originCountry?: string;
  displayOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;
};

export type GetDelegateListParams = {
  delegateName?: string;
  page: number; // 0-based
  size: number;
  order: string; // "createdAt,desc"
};

export type DelegateUpsertBody = {
  delegateName: string;
  delegateDesc: string;
};

function buildMultipart(body: DelegateUpsertBody, file?: File | null) {
  const fd = new FormData();
  // ✅ 서버가 @RequestPart("data") DTO 같은 걸로 받는 케이스면 이게 정석
  fd.append("data", new Blob([JSON.stringify(body)], { type: "application/json" }));
  if (file) fd.append("file", file);
  return fd;
}

export const getDelegateListApi = async (
  params: GetDelegateListParams
): Promise<ApiResponse<PageContent<DelegateListItem>>> => {
  const res = await axiosInstance.get<ApiResponse<PageContent<DelegateListItem>>>(
    "/v1/admin/partner/delegate/list",
    { params }
  );
  return res.data;
};

// DETAIL
export const getDelegateApi = async (id: number): Promise<ApiResponse<DelegateItem>> => {
  const res = await axiosInstance.get<ApiResponse<DelegateItem>>(
    `/v1/admin/partner/delegate/${id}`
  );
  return res.data;
};

// CREATE (multipart)
export const createDelegateApi = async (
  body: DelegateUpsertBody,
  file?: File | null
): Promise<ApiResponse<DelegateItem>> => {
  const fd = buildMultipart(body, file);
  const res = await axiosInstance.post<ApiResponse<DelegateItem>>("/v1/admin/partner/delegate", fd);
  return res.data;
};

// UPDATE (multipart)
export const updateDelegateApi = async (
  id: number,
  body: DelegateUpsertBody,
  file?: File | null
): Promise<ApiResponse<DelegateItem>> => {
  const fd = buildMultipart(body, file);
  const res = await axiosInstance.put<ApiResponse<DelegateItem>>(
    `/v1/admin/partner/delegate/${id}`,
    fd
  );
  return res.data;
};

// DELETE
export const deleteDelegateApi = async (id: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`/v1/admin/partner/delegate/${id}`);
  return res.data;
};
